import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import NextTrainingCard from '../components/NextTrainingCard';
import { calculateProgressPercentage } from '../utils/progressCalculation';
import { useQuery } from '@tanstack/react-query';
import { portalCandidateService } from '../api/portalCandidateService';
import { churchDirectoryService } from '../api/churchDirectoryService';
import { planterService } from '../api/planterService';
import { fetchMyMbcbReports } from '../api/mbcbReportService';
import { mbcbMinistryPeriodCaption, mbcbReportNamedTitle } from '../utils/mbcbReportPeriodLabels';

const formatUsd = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n || 0);

const formatInt = (n: number) => new Intl.NumberFormat('en-US').format(n || 0);

function NetworkMetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 shadow-sm">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-lg sm:text-xl font-semibold text-gray-900 tabular-nums">{value}</div>
      {hint ? <p className="mt-1 text-xs text-gray-500 leading-snug">{hint}</p> : null}
    </div>
  );
}

type AssessmentInfoResponse = {
  success: boolean;
  data: Array<{
    assessment?: {
      id: number;
      name?: string;
      date?: string;
      location?: string | null;
    };
  }>;
};

/** API may return roles as string[] (JWT) or { name }[] from /auth/me */
function userHasRole(
  u: { roles?: Array<string | { name?: string }> } | null | undefined,
  roleName: string,
): boolean {
  if (!u?.roles?.length) return false;
  return u.roles.some((r) => (typeof r === 'string' ? r : r?.name) === roleName);
}

const Dashboard: React.FC = () => {
  const { portalCandidate, user, isInvitedPlanterOnly, isAuthenticated } = useAuth();
  const { data: assessmentInfo } = useQuery({
    queryKey: ['portalCandidate', 'assessmentInfo'],
    queryFn: async () => (await portalCandidateService.getAssessmentInfo()) as AssessmentInfoResponse,
    enabled: !!portalCandidate,
  });
  const { data: churchProfile } = useQuery({
    queryKey: ['myChurchProfile'],
    queryFn: async () => {
      try {
        return await churchDirectoryService.getMyChurchProfile();
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!user?.planterProfile,
    retry: false,
  });
  const { data: planterProfile } = useQuery({
    queryKey: ['myPlanterProfile'],
    queryFn: async () => {
      try {
        return await planterService.getMyPlanter();
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!user?.planterProfile,
    retry: false,
  });
  const { data: networkImpact, isLoading: networkImpactLoading, isError: networkImpactError } = useQuery({
    queryKey: ['planters', 'network-impact'],
    queryFn: () => planterService.getNetworkImpact(),
    enabled: isAuthenticated && userHasRole(user, 'church_planter'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: mbcbReportsList } = useQuery({
    queryKey: ['mbcbReports', 'me'],
    queryFn: fetchMyMbcbReports,
    enabled:
      isAuthenticated &&
      userHasRole(user, 'church_planter') &&
      !!planterProfile &&
      planterProfile.mbcbReportingEnabled !== false,
    retry: false,
    staleTime: 60 * 1000,
  });

  const incompleteMbcbReports = React.useMemo(() => {
    if (!mbcbReportsList?.length) return [];
    return mbcbReportsList.filter(
      (r) => r.status === 'draft' || r.status === 'needs_revision',
    );
  }, [mbcbReportsList]);

  // Only ever populated once someone on the assessment team publishes a report.
  const { data: publishedReports } = useQuery({
    queryKey: ['portalCandidate', 'assessmentFinalReports'],
    queryFn: () => portalCandidateService.getAssessmentFinalReports(),
    enabled: !!portalCandidate,
    retry: false,
  });
  const hasPublishedAssessmentReport = (publishedReports?.data?.length ?? 0) > 0;

  const latestAssessment = assessmentInfo?.data?.[0]?.assessment;
  const assessmentStartDate = latestAssessment?.date ? new Date(latestAssessment.date) : null;
  const assessmentEndDate = assessmentStartDate
    ? new Date(new Date(assessmentStartDate).setDate(assessmentStartDate.getDate() + 1))
    : null;
  const hubLeaderName = planterProfile?.hubLeader
    ? [planterProfile.hubLeader.firstName, planterProfile.hubLeader.lastName].filter(Boolean).join(' ').trim()
    : '';


  // Calculate portal completion percentage (candidate-owned)
  const calculateCompletionPercentage = () => {
    if (!portalCandidate) return 0;
    
    // Use the unified progress calculation utility that matches backend logic
    // Map portalCandidate fields to the expected format
    const candidateData = {
      profileCompleted: portalCandidate.profileCompleted || (portalCandidate.onboardingStep && portalCandidate.onboardingStep > 1),
      whyChurchPlantingCompleted: (portalCandidate as any).whyChurchPlantingCompleted || false,
      theologyCompleted: portalCandidate.theologyCompleted || false,
      referencesCompleted: portalCandidate.referencesCompleted || false,
      sendingChurchCompleted: portalCandidate.sendingChurchCompleted || false,
      maritalStatus: portalCandidate.maritalStatus,
      prepareEnrichStatus: portalCandidate.prepareEnrichStatus,
      prepareEnrichRequested: (portalCandidate as any).prepareEnrichRequested || false,
      prepareEnrichData: (portalCandidate as any).prepareEnrichData,
      spouseInterviewSubmitted: (portalCandidate as any).spouseInterviewSubmitted || false,
      spouseInterviewData: (portalCandidate as any).spouseInterviewData,
      spouseInterviewStatus: (portalCandidate as any).spouseInterviewStatus,
      ministryTransitionsCompleted: (portalCandidate as any).ministryTransitionsCompleted || false,
      backgroundCheckStatus: portalCandidate.backgroundCheckStatus,
      spiritualHealthCompleted: portalCandidate.spiritualHealthCompleted || false,
      spiritualHealthCandidateSubmitted: portalCandidate.spiritualHealthCandidateSubmitted || false,
      spiritualHealthSpouseSubmitted: portalCandidate.spiritualHealthSpouseSubmitted || false,
    };
    
    return calculateProgressPercentage(candidateData);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Welcome Header */}
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Welcome,{' '}
              {isInvitedPlanterOnly
                ? user?.firstName || user?.email || 'there'
                : portalCandidate?.firstName || portalCandidate?.name?.split(' ')[0] || 'Candidate'}
              !
            </h1>
            <p className="text-gray-600 mt-2">
              {isInvitedPlanterOnly
                ? 'Use the sections below for your hub, church directory profile, and MBCB reporting.'
                : portalCandidate
                  ? 'Track your application progress and see what steps remain to complete your onboarding.'
                  : 'We could not load your application profile. Try refreshing the page, or contact support if this continues.'}
            </p>
          </div>

          {incompleteMbcbReports.length > 0 && (
            <div
              className={`rounded-xl border-2 p-4 sm:p-6 shadow-md ${
                incompleteMbcbReports.some((r) => r.status === 'needs_revision')
                  ? 'border-amber-300 bg-amber-50/90'
                  : 'border-primary-200 bg-primary-50/80'
              }`}
            >
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                {incompleteMbcbReports.length === 1
                  ? 'Finish your monthly report'
                  : 'Reports needing your attention'}
              </h2>
              <p className="text-sm text-gray-700 mt-1">
                Each line is named for when you submit (e.g. &quot;April report&quot;); the content is for the prior
                calendar month of ministry. Your story helps your team know how to pray and where to send help.
              </p>
              <ul className="mt-4 space-y-3">
                {incompleteMbcbReports.map((r) => {
                  const isRevision = r.status === 'needs_revision';
                  return (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white/80 border border-gray-200/80 px-3 py-3 sm:px-4"
                    >
                      <div className="flex flex-col min-w-0 gap-0.5">
                        <span className="font-semibold text-gray-900">
                          {mbcbReportNamedTitle(r.reportMonth, r.reportYear)}
                        </span>
                        <span className="text-xs text-gray-600">
                          {mbcbMinistryPeriodCaption(r.reportMonth, r.reportYear)}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            isRevision
                              ? 'bg-amber-100 text-amber-900'
                              : 'bg-primary-100 text-primary-900'
                          }`}
                        >
                          {isRevision ? 'Needs revision' : 'Draft'}
                        </span>
                      </div>
                      <Link
                        to={`/mbcb/report/${r.id}`}
                        className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isRevision
                            ? 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500'
                            : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500'
                        }`}
                      >
                        Continue report
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {isAuthenticated && userHasRole(user, 'church_planter') && !networkImpactError && (
            <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">You&apos;re part of a larger work</h2>
              <p className="text-sm text-gray-600 mt-1">
                A snapshot of God&apos;s work across the 242 Network—not only your church, but the whole family of
                plants reporting together.
              </p>
              {networkImpactLoading ? (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[0, 1, 2].map((k) => (
                    <div key={k} className="rounded-lg border border-gray-100 bg-gray-100 h-24 animate-pulse" />
                  ))}
                </div>
              ) : networkImpact ? (
                <>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <NetworkMetricCard
                      label="Baptisms reported (network)"
                      value={formatInt(networkImpact.totalBaptisms)}
                      hint="Total from MBCB reports across the network."
                    />
                    <NetworkMetricCard
                      label="Plants in funding or post-funding"
                      value={formatInt(networkImpact.plantsInFundingOrPostFunding)}
                      hint="Active church plants currently in funding or post-funding steps."
                    />
                    <NetworkMetricCard
                      label="Cooperative Program giving (network)"
                      value={formatUsd(networkImpact.totalCooperativeProgramGiving)}
                      hint="CP missions line items summed from MBCB reports."
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-4 leading-snug">
                    Network-wide totals; all-time figures from MBCB reports with status:{' '}
                    {networkImpact.mbcbReportStatusesIncluded
                      .map((s) => s.replace(/_/g, ' '))
                      .join(', ')}
                    .
                  </p>
                </>
              ) : null}
            </div>
          )}

          {isInvitedPlanterOnly ? null : portalCandidate ? (
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
                Portal Progress
              </h2>
              <div>
                <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-gray-500">
                  <span>Overall Progress</span>
                  <span>{calculateCompletionPercentage()}%</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      calculateCompletionPercentage() === 100 ? 'bg-green-600' : 'bg-primary-600'
                    }`}
                    style={{ width: `${calculateCompletionPercentage()}%` }}
                  ></div>
                </div>
              </div>
              <div className="mt-6">
                <Link
                  to="/onboarding"
                  className="w-full inline-flex justify-center items-center py-3 sm:py-4 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  Continue Onboarding
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 sm:p-6">
              <div className="flex justify-center mb-4">
                <svg className="w-12 h-12 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-medium text-yellow-800 mb-2 sm:mb-3 text-center">
                Complete Your Profile
              </h3>
              <p className="text-sm sm:text-base text-yellow-700 mb-4 sm:mb-6 text-center">
                It looks like your portal candidate profile hasn't been created yet. 
                This may be due to a registration issue.
              </p>
              <button
                className="w-full flex justify-center py-3 sm:py-4 px-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                onClick={() => window.location.reload()}
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Page
              </button>
            </div>
          )}

          {user?.planterProfile && (
            <>
              <NextTrainingCard />
              {planterProfile?.assignedHub && (
                <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                    Hub Assignment
                  </h2>
                  <div className="space-y-3 text-sm sm:text-base">
                    <div>
                      <p className="text-gray-500">Hub</p>
                      <p className="text-gray-900 font-medium">{planterProfile.assignedHub}</p>
                    </div>
                    {hubLeaderName && (
                      <div>
                        <p className="text-gray-500">Hub Leader</p>
                        <p className="text-gray-900 font-medium">{hubLeaderName}</p>
                      </div>
                    )}
                    {planterProfile.hubLeader?.email && (
                      <div>
                        <p className="text-gray-500">Email</p>
                        <a
                          href={`mailto:${planterProfile.hubLeader.email}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {planterProfile.hubLeader.email}
                        </a>
                      </div>
                    )}
                    {planterProfile.hubLeader?.phone && (
                      <div>
                        <p className="text-gray-500">Phone</p>
                        <a
                          href={`tel:${planterProfile.hubLeader.phone}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {planterProfile.hubLeader.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Church Directory Profile
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                {churchProfile
                  ? 'Review or update the church details hub users see in the directory.'
                  : 'Complete the church profile that will appear in the hub directory.'}
              </p>
              <div className="mt-4">
                <Link
                  to="/church-profile"
                  className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  {churchProfile ? 'View Profile' : 'Complete Profile'}
                </Link>
              </div>
            </div>
            </>
          )}

          {assessmentStartDate && assessmentEndDate && (
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Assessment Details
              </h2>
              <div className="flex items-center gap-4 mt-3">
                <div className="w-16 rounded-lg border border-gray-200 overflow-hidden text-center shadow-sm">
                  <div className="bg-primary-600 text-white text-xs font-semibold py-1">
                    {assessmentStartDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                  </div>
                  <div className="py-2 text-xl font-bold text-gray-900">
                    {assessmentStartDate.getDate()}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {assessmentStartDate.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}{' '}
                    –{' '}
                    {assessmentEndDate.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  {latestAssessment?.location && (
                    <p className="text-sm text-gray-600 mt-1">{latestAssessment.location}</p>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to="/assessment-schedule"
                  className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  Go to Assessment Area
                </Link>
              </div>
            </div>
          )}

          {hasPublishedAssessmentReport && (
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Your Assessment Report
              </h2>
              <p className="text-sm text-gray-600">
                The assessment team has shared your report and growth recommendations with you.
              </p>
              <div className="mt-4">
                <Link
                  to="/assessment-report"
                  className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  Read Your Report
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
